import * as React from "react"
import * as TooltipPrimitive from "@radix-ui/react-tooltip"
import { cn } from "@/lib/utils"

export interface ListTooltipItem {
  id: string
  label: string
  sublabel?: string
}

export interface ListTooltipProps {
  /** The trigger element that shows the tooltip on hover */
  children: React.ReactNode
  /** Title shown at the top of the tooltip */
  title?: string
  /** Array of items to display in the list */
  items: ListTooltipItem[]
  /** Message to show when there are no items */
  emptyMessage?: string
  /** Positioning of the tooltip relative to trigger */
  side?: "top" | "right" | "bottom" | "left"
  /** Alignment of the tooltip */
  align?: "start" | "center" | "end"
  /** Additional class for the tooltip content */
  className?: string
  /** Delay in ms before tooltip appears */
  delayDuration?: number
}

/**
 * ListTooltip - A Linear-inspired tooltip component for displaying lists of items.
 * Designed as a reusable component following the design system principles:
 * - Clean white background with subtle border (#EDEEF3)
 * - Smooth scale-in/fade-in animations
 * - 8pt grid spacing system
 * - LCH-based color palette
 */
export function ListTooltip({
  children,
  title,
  items,
  emptyMessage = "No items",
  side = "bottom",
  align = "start",
  className,
  delayDuration = 200,
}: ListTooltipProps) {
  return (
    <TooltipPrimitive.Provider delayDuration={delayDuration}>
      <TooltipPrimitive.Root>
        <TooltipPrimitive.Trigger asChild>
          {children}
        </TooltipPrimitive.Trigger>
        <TooltipPrimitive.Portal>
          <TooltipPrimitive.Content
            side={side}
            align={align}
            sideOffset={6}
            className={cn(
              // Base container styles following Linear design
              "z-50 min-w-[160px] max-w-[280px]",
              // Background and border - clean white with subtle border
              "bg-white border border-[#EDEEF3] rounded-[8px]",
              // Shadow for elevation
              "shadow-[0_4px_16px_rgba(0,0,0,0.08),0_1px_4px_rgba(0,0,0,0.04)]",
              // Padding following 8pt grid
              "p-[12px]",
              // Animation - scale and fade in from origin, similar to Linear's snappy feel
              "animate-in fade-in-0 zoom-in-95",
              "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
              // Directional slide animations
              "data-[side=bottom]:slide-in-from-top-2",
              "data-[side=top]:slide-in-from-bottom-2",
              "data-[side=left]:slide-in-from-right-2",
              "data-[side=right]:slide-in-from-left-2",
              // Origin point for scale animation
              "origin-[var(--radix-tooltip-content-transform-origin)]",
              className
            )}
          >
            {/* Optional title header */}
            {title && (
              <div className="text-[11px] font-medium text-[#8A8F98] uppercase tracking-wide mb-[8px] pb-[8px] border-b border-[#EDEEF3]">
                {title}
              </div>
            )}
            
            {/* List content */}
            <div className="space-y-[4px]">
              {items.length === 0 ? (
                <span className="text-[12px] text-[#8A8F98] italic">
                  {emptyMessage}
                </span>
              ) : (
                items.map((item) => (
                  <div key={item.id} className="flex flex-col">
                    <span className="text-[12px] text-[#08090A] leading-[18px]">
                      {item.label}
                    </span>
                    {item.sublabel && (
                      <span className="text-[11px] text-[#8A8F98] leading-[16px]">
                        {item.sublabel}
                      </span>
                    )}
                  </div>
                ))
              )}
            </div>
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  )
}

export default ListTooltip

